import { useState } from "react";
import { ThumbsUp, ThumbsDown, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitFeedback } from "@/lib/api";

export function FeedbackWidget({ page, streamId, assessmentId }) {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      await submitFeedback({ rating, comment, page, streamId, assessmentId });
      setDone(true);
    } catch {
      toast.error("Could not send your feedback. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mt-8 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="feedback-done">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> Thanks — your feedback helps us keep PR Pathfinder accurate.
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid="feedback-widget">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">Was this result helpful?</p>
          <p className="text-xs text-slate-500">Anonymous — one tap is enough, a comment is optional.</p>
        </div>
        <div className="flex gap-2">
          <RateButton kind="up" active={rating === "up"} onClick={() => setRating("up")} />
          <RateButton kind="down" active={rating === "down"} onClick={() => setRating("down")} />
        </div>
      </div>
      {rating && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 1000))} rows={2} data-testid="feedback-comment"
            placeholder={rating === "up" ? "What worked well? (optional)" : "What was wrong or missing? (optional)"}
            className="min-h-[48px] flex-1 resize-y rounded-md border border-slate-300 px-3 py-2 text-base focus:border-[#C8102E] focus:outline-none focus:ring-2 focus:ring-[#C8102E]/20 sm:text-sm" />
          <button onClick={send} disabled={busy} data-testid="feedback-submit"
            className="inline-flex min-h-[48px] items-center justify-center gap-1.5 rounded-md bg-[#0F172A] px-4 text-sm font-semibold text-white hover:bg-[#1E293B] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send feedback
          </button>
        </div>
      )}
    </div>
  );
}

function RateButton({ kind, active, onClick }) {
  const Icon = kind === "up" ? ThumbsUp : ThumbsDown;
  const on = kind === "up" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-rose-500 bg-rose-50 text-rose-700";
  return (
    <button type="button" onClick={onClick} aria-pressed={active} aria-label={kind === "up" ? "Helpful" : "Not helpful"} data-testid={`feedback-${kind}`}
      className={`flex h-12 w-12 items-center justify-center rounded-full border transition-colors ${active ? on : "border-slate-300 bg-white text-slate-500 hover:border-slate-400"}`}>
      <Icon className="h-5 w-5" />
    </button>
  );
}
